import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import PageHeading from "./PageHeading";

const ProfilePage = () => {
  const [user, setUser] = useState({});
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/signin");
      return;
    }

    const fetchUserData = async () => {
      try {
        const response = await axios.get("https://api.muktihospital.com/api/auth/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const userData = response.data;
        setUser(userData);
        setName(userData.name || "");
        setMobile(userData.mobile || "");
        setEmail(userData.email || "");
        if (userData.profilePhoto) {
          setImagePreview(`https://api.muktihospital.com${userData.profilePhoto}`);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      toast.error("File size exceeds 5MB limit");
      return;
    }
    setProfilePhoto(file);
    setImageFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(user.profilePhoto ? `https://api.muktihospital.com${user.profilePhoto}` : null);
      setProfilePhoto(null);
      setImageFile(null);
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  const formData = new FormData();
  formData.append("name", name);
  formData.append("mobile", mobile);
  formData.append("email", email);

  // Only append profilePhoto if a new file is selected
  if (profilePhoto) {
    formData.append("profilePhoto", profilePhoto);
  }

  try {
    const response = await axios.put(
      "https://api.muktihospital.com/api/user/update-profile",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      }
    );

    setUser(response.data.user);
    if (response.data.user.profilePhoto) {
      setImagePreview(`https://api.muktihospital.com${response.data.user.profilePhoto}`);
    }
    toast.success("Profile updated successfully");
  } catch (error) {
    console.error("Error updating profile:", error);
    toast.error("Failed to update profile");
  } finally {
    setIsSubmitting(false);
  }
};

  const breadcrumbs = [
    { label: "Dashboard", url: "/dashboard" },
    { label: "Manage Patients" },
    { label: "User Management" },
  ];

  if (loading) {
    return (
      <SkeletonTheme baseColor="#e5e7eb" highlightColor="#f3f4f6">
        <div className="p-4">
          <Skeleton height={40} width={300} className="mb-4" />
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white shadow-lg rounded-lg p-5">
              <Skeleton height={30} width={200} className="mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </div>
            </div>
            <div className="bg-white shadow-lg rounded-lg p-5">
              <Skeleton height={30} width={200} className="mb-4" />
              <Skeleton circle={true} height={96} width={96} className="mb-4" />
              <Skeleton height={40} />
              <Skeleton height={20} width={150} />
            </div>
          </div>
          <Skeleton height={40} width={100} className="mt-4 ml-auto" />
        </div>
      </SkeletonTheme>
    );
  }

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <PageHeading title="Update Profile" breadcrumbs={breadcrumbs} />
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-6 items-start">
          <div className="bg-white shadow-lg rounded-lg">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Basic Information
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                  className="input-field"
                  required
                />
              </div>
             
              <div>
                <label className="label">Mobile Number</label>
                <input
                  type="text"
                  name="mobile"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="Mobile"
                  className="input-field"
                  required
                />
              </div>
            </div>
          </div>
          <div className="bg-white shadow-lg rounded-lg">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Profile Photo
              </h2>
            </div>
            <div className="p-5">
              {imagePreview && (
                <div className="mb-4">
                  <img
                    src={imagePreview}
                    alt="User Profile Preview"
                    className="w-24 h-24 rounded-full object-cover border shadow"
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                className="border p-2 rounded w-full"
                onChange={handleFileChange}
                disabled={isSubmitting}
              />
              <p className="text-sm text-gray-600 mt-1">
                Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB.
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-4 pt-2">
          <button
            type="submit"
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfilePage;